// V2C1 compatibility contract only. NOT connected to Book Workspace, API or UI.
// The backend returns verified boolean checkpoints; V2A expects fingerprinted
// checkpoint objects. Translate only exact owner-authorized server responses.
// Never infer confirmations from project status, page visits or local storage.
import { novelContextualJourneyV2 } from "./novelContextualJourneyV2";

const clean = (value) => String(value ?? "").trim();
const CANONICAL_ID = /^[a-f0-9]{24}$/i;
const FINGERPRINT = /^[a-f0-9]{64}$/;
const ACKNOWLEDGMENTS = Object.freeze({
  manuscript: "CONFIRM MANUSCRIPT READY FOR REVIEW",
  review: "CONFIRM EDITORIAL REVIEW COMPLETE",
});

// A frontend flag is only presentation gating, NEVER security authorization.
// The server independently authenticates the saved exact pilot book and owner.
export function novelJourneyPilotEligibleV2C1({
  enabled = false, configuredPilotId = "", canonicalStoryId = "",
  storyType = "", isOwner = false, isStructurePreview = false,
} = {}) {
  const saved = clean(canonicalStoryId);
  const pilot = clean(configuredPilotId);
  return enabled === true && isOwner === true && isStructurePreview !== true &&
    CANONICAL_ID.test(saved) && CANONICAL_ID.test(pilot) &&
    saved.toLowerCase() === pilot.toLowerCase() &&
    clean(storyType).toLowerCase() === "longform";
}

export function novelJourneyPilotReadV2C1(payload) {
  if (payload?.ok !== true || payload?.readOnly !== true ||
      !FINGERPRINT.test(clean(payload?.sourceFingerprint)) ||
      typeof payload?.manuscript !== "boolean" ||
      typeof payload?.review !== "boolean" ||
      (payload.review && !payload.manuscript) ||
      !Number.isSafeInteger(payload?.revision) || payload.revision < 0) {
    return { ok: false, code: "verified-owner-progress-unavailable" };
  }
  // The owner API may include V2C17 saved-format evidence. Older backend
  // deployments omit both fields; preserve the V2C16 inspection-only fallback.
  const claimsFormat = Object.prototype.hasOwnProperty.call(payload,"formatReady") ||
    Object.prototype.hasOwnProperty.call(payload,"formatFingerprint");
  const reportsFormat = typeof payload.formatReady === "boolean";
  if ((claimsFormat && !reportsFormat) || (reportsFormat &&
      ((payload.formatReady && !FINGERPRINT.test(clean(payload.formatFingerprint))) ||
       (!payload.formatReady && payload.formatFingerprint != null &&
        clean(payload.formatFingerprint) !== "")))) {
    return {ok:false,code:"verified-format-progress-unavailable"};
  }
  const sourceFingerprint = clean(payload.sourceFingerprint);
  // Only manuscript and review are currently persisted by V2B6. Never fake
  // preparation, layout, private preview, PDF approval or publication stages.
  return {
    ok: true, sourceFingerprint, revision: payload.revision,
    manuscript: payload.manuscript, review: payload.review,
    formatReady: reportsFormat ? payload.formatReady : null,
    formatFingerprint: reportsFormat && payload.formatReady
      ? clean(payload.formatFingerprint) : "",
    checkpoints: {
      manuscript: payload.manuscript ? { confirmed: true, sourceFingerprint } : null,
      review: payload.review ? { confirmed: true, sourceFingerprint } : null,
    },
  };
}

export function novelJourneyPilotRecommendationV2C1({
  eligible = false, response, publicationStatus = "inDevelopment",
  chapterCount = 0, sceneCount = 0, readiness = {},
  formatFingerprint = "", previewEnabled = false, previewBusy = false,
  finalApprovalEnabled = false,
} = {}) {
  if (eligible !== true) return { ok: false, code: "not-in-pilot" };
  const saved = novelJourneyPilotReadV2C1(response);
  if (!saved.ok) return { ok: false, code: saved.code, unavailable: true };
  // A server assertion of incomplete saved print format takes precedence over
  // UI-only planning checks. A valid format fingerprint permits inspecting the
  // existing preparation workspace, not approving any later checkpoint.
  // V2C18: When available, authenticated saved-format evidence outranks a
  // stale local planning calculation in BOTH directions. The independent
  // production-readiness gate remains unchanged.
  const currentReadiness = saved.formatReady === null
    ? readiness : {...readiness,formatReady:saved.formatReady};
  const currentFormatFingerprint = saved.formatReady === true
    ? saved.formatFingerprint
    : saved.formatReady === false ? "" : formatFingerprint;
  const next = novelContextualJourneyV2({
    publicationStatus, chapterCount, sceneCount, readiness:currentReadiness,
    checkpoints: saved.checkpoints,
    sourceFingerprint: saved.sourceFingerprint,
    formatFingerprint:currentFormatFingerprint,
    previewEnabled, previewBusy, finalApprovalEnabled,
  });
  // V2C16: The backend attests manuscript and review, NOT a saved format
  // fingerprint. After both author decisions and existing saved-format checks,
  // send the owner to the established Publication Preparation checklist for
  // *inspection*. Never invent a format/preparation/layout/PDF approval stamp.
  // Incomplete format remains at Book Setup; stale source or missing review
  // is handled by the earlier V2A guards without any shortcut.
  const needsPublicationInspection =
    saved.manuscript === true &&
    saved.review === true &&
    saved.formatReady === null &&
    readiness?.formatReady === true &&
    next.reason === "format-verification-unknown";
  // Preparation is not a persisted checkpoint yet. Do not present a missing
  // confirmation button as the author's next action, and do not advance to
  // Layout or PDF based on saved-format evidence or page visits.
  const needsVerifiedPreparationInspection =
    saved.manuscript === true &&
    saved.review === true &&
    saved.formatReady === true &&
    next.reason === "author-preparation-confirmation-required";
  const recommended = needsPublicationInspection
    ? {
        id: "publication-preparation",
        reason: "publication-readiness-inspection-required",
        advisoryOnly: true,
        formatVerificationPending: true,
      }
    : needsVerifiedPreparationInspection
    ? {
        id: "publication-preparation",
        reason: "verified-publication-preparation-inspection-required",
        advisoryOnly: true,
        formatVerified: true,
      }
    : next;
  // Later preparation/layout/preview confirmations have not been implemented.
  // Guidance may open their workspaces, but cannot claim they are complete.
  const unsupportedCheckpoint = [
    "publication-readiness-inspection-required",
    "verified-publication-preparation-inspection-required",
    "author-preparation-confirmation-required",
    "author-layout-confirmation-required",
    "private-preview-review-required",
  ].includes(recommended.reason);
  return {
    ok: true, sourceFingerprint: saved.sourceFingerprint,
    manuscriptConfirmed: saved.manuscript,
    reviewConfirmed: saved.review,
    revision: saved.revision,
    next: { ...recommended, pendingFutureCheckpoint: unsupportedCheckpoint },
  };
}

export function novelJourneyPilotConfirmationV2C1({
  eligible = false, response, stage,
} = {}) {
  if (eligible !== true) return { ok: false, code: "not-in-pilot" };
  const saved = novelJourneyPilotReadV2C1(response);
  if (!saved.ok) return { ok: false, code: saved.code };
  if (!Object.prototype.hasOwnProperty.call(ACKNOWLEDGMENTS, stage)) {
    return { ok: false, code: "unknown-checkpoint-stage" };
  }
  if (stage === "review" && saved.manuscript !== true) {
    return { ok: false, code: "manuscript-confirmation-required" };
  }
  return {
    ok: true,
    request: {
      stage,
      acknowledgment: ACKNOWLEDGMENTS[stage],
      expectedSourceFingerprint: saved.sourceFingerprint,
    },
  };
}
